import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import Highcharts from 'highcharts'
import HighStockcharts from 'highcharts/highstock'
import HighchartsReact from 'highcharts-react-official'
import { format, subYears, subMonths, lastDayOfYear, endOfMonth, startOfDay } from 'date-fns'

import Grid from '../../atoms/FoundationGrid/Grid'
import Cell from '../../atoms/FoundationGrid/Cell'
import Switch from '../../atoms/Switch'
import DatePicker from '../DatePicker'
import commonConfig from './common.config.json'

import './style.scss'

const CustomHighchart = ({
  id,
  commonOptions,
  options,
  chartType,
  hasDateRange,
  hasDatePicker,
  extraClasses,
  onChartRangeChange,
  ...restProps
}) => {
  const { rangeSelectorOptions, topOptions, displayMinMax, startFromZero, updateDate, dataDurationType, ...restOptions } = options
  const savedSeries = JSON.parse(JSON.stringify(restOptions.series))
  const [defaultSelectedRangeId, setDefaultSelectedRangeId] = useState(rangeSelectorOptions?.selectedId || 'all')

  const tickIntervalMapping = {
    '1m': 2629800000, // 3600 * 24 * 30 * 1000,
    '3m': 7776000000, // 3600 * 24 * 30 * 3 * 1000,
    '6m': 15778800000, // 3600 * 24 * 30 * 6 * 1000,
    '1y': 31557600000 // 3600 * 24 * 30 * 12 * 1000
  }

  let defaultXAxisInterval = null

  const chartComponentRef = useRef(null)

  const getMinMaxPoints = (series) => {
    const labelFormatter = (flag, name, value) => {
      const prefixContent = flag === 'min' ? 'Min' : 'Max'
      return `
        <div>
          <b>${name}</b>
          <span>${prefixContent} Value: ${value}</span>
        </div>
      `
    }

    const getMinMaxPoint = (flag, item, name) => {
      return {
        x: item[0],
        y: item[1],
        dataLabels: {
          enabled: true,
          useHTML: true,
          formatter: () => {
            return labelFormatter(flag, name, item[1])
          }
        },
        marker: { enabled: true, radius: 5 }
      }
    }

    const copiedSeries = JSON.parse(JSON.stringify(series))

    const minMaxPoints = copiedSeries.map((seriesItem) => {
      const minItem = _.minBy(seriesItem.data, (item) => item[1])
      const maxItem = _.maxBy(seriesItem.data, (item) => item[1])

      return { min: getMinMaxPoint('min', minItem, seriesItem.name), max: getMinMaxPoint('max', maxItem, seriesItem.name) }
    })

    return minMaxPoints
  }

  const formatMinMaxSeries = (minMaxPoints) => {
    const copiedSeries = JSON.parse(JSON.stringify(savedSeries))

    const formattedSeries = copiedSeries.map((seriesItem, seriesItemIndex) => {
      const copiedSeriesItem = JSON.parse(JSON.stringify(seriesItem))
      const { min, max } = minMaxPoints[seriesItemIndex]

      const minIndex = _.findIndex(seriesItem.data, (dataItem) => {
        return dataItem[0] === min.x && dataItem[1] === min.y
      })

      const maxIndex = _.findIndex(seriesItem.data, (dataItem) => {
        return dataItem[0] === max.x && dataItem[1] === max.y
      })

      copiedSeriesItem.data[minIndex] = min
      copiedSeriesItem.data[maxIndex] = max

      return {
        ...copiedSeriesItem,
        turboThreshold: 0
      }
    })

    return formattedSeries
  }

  const yearsDurationTickIntervalMapping = [
    {
      years: '1y',
      duration: 31557600000, // 3600 * 24 * 30 * 12 * 1000,
      tickInterval: '1m'
    },
    {
      years: '3y',
      duration: 31557600000 * 3, // 3600 * 24 * 30 * 12 * 3 * 1000,
      tickInterval: '3m'
    },
    {
      years: '5y',
      duration: 31557600000 * 5, // 3600 * 24 * 30 * 12 * 5 * 1000,
      tickInterval: '6m'
    },
    {
      years: '10y',
      duration: 31557600000 * 10, // 3600 * 24 * 30 * 12 * 10 * 1000,
      tickInterval: '1y'
    }
  ]

  const defaultRangeSeletorButtons = [
    {
      type: 'ytd',
      id: 'ytd',
      text: 'YTD',
      title: 'ytd',
      xInterval: '1m'
    },
    {
      type: 'month',
      id: '3m',
      count: 3,
      text: '3m',
      title: '3m',
      xInterval: '1m'
    },
    {
      type: 'month',
      id: '6m',
      count: 6,
      text: '6m',
      title: '6m',
      xInterval: '1m'
    },
    {
      type: 'year',
      id: '1y',
      count: 1,
      text: '1y',
      title: '1y',
      xInterval: '1m'
    },
    {
      type: 'year',
      id: '2y',
      count: 2,
      text: '2y',
      title: '2y',
      xInterval: '1m'
    },
    {
      type: 'year',
      id: '3y',
      count: 3,
      text: '3y',
      title: '3y',
      xInterval: '3m'
    },
    {
      type: 'year',
      id: '5y',
      count: 5,
      text: '5y',
      title: '5y',
      xInterval: '6m'
    },
    {
      type: 'year',
      id: '10y',
      count: 10,
      text: '10y',
      title: '10y',
      xInterval: '1y'
    },
    {
      type: 'all',
      id: 'all',
      text: 'All',
      title: 'all',
      xInterval: 'all'
    }
  ]

  const [highchartConfig, setHighchartConfig] = useState()
  const [rangeSelectorConfig, setRangeSelectorConfig] = useState()
  const [datePickerValues, setDatePickerValues] = useState()
  const [minMaxDates, setMinMaxDates] = useState()

  const rangeSelectorConfigRef = useRef(rangeSelectorConfig)

  const setRangeSelectorConfigRef = (x) => {
    rangeSelectorConfigRef.current = x
    setRangeSelectorConfig(x)
  }

  const getIndexOfSelectedRangeSelectorButton = ({ buttons = defaultRangeSeletorButtons, rangeSelectorId = defaultSelectedRangeId }) =>
    _.findIndex(buttons, (buttonItem) => buttonItem.id === rangeSelectorId)

  const updateRangeSelectorButtonsStatus = ({ isClean, itemId }) => {
    const { buttons } = rangeSelectorConfigRef.current
    const copiedButtons = buttons.map((button) => {
      return {
        ...button,
        checked: isClean ? false : itemId === button.id
      }
    })

    if (isClean) {
      setDefaultSelectedRangeId('none')
    } else {
      setDefaultSelectedRangeId(itemId)
    }

    return { buttons: copiedButtons, selected: isClean ? -1 : getIndexOfSelectedRangeSelectorButton({ buttons, rangeSelectorId: itemId }) }
  }

  const handleRangeButtonClick = ({ item }) => {
    const currentHighchartInstance = chartComponentRef.current.chart
    const { max, dataMin, dataMax } = currentHighchartInstance.xAxis[0]
    const { type, count } = item
    let newMin = dataMin
    let newMax = dataMax

    if (type === 'year') {
      newMin = parseFloat(format(subYears(max, count), 'TT'))
      newMax = max
    } else if (type === 'month') {
      newMin = parseFloat(format(subMonths(max, count), 'TT'))
      newMax = max
    } else if (type === 'ytd') {
      newMin = parseFloat(format(lastDayOfYear(subYears(dataMax, 1)), 'TT'))
      newMax = dataMax
    }

    setRangeSelectorConfigRef(updateRangeSelectorButtonsStatus({ itemId: item.id }))
    currentHighchartInstance.xAxis[0].setExtremes(newMin, newMax)
    onChartRangeChange(item)
  }

  const getRangeSelectorConfig = () => {
    if (rangeSelectorOptions) {
      const { rangeSelectorButtonsContent, rangeSelectorButtons, selectedId } = rangeSelectorOptions

      if (rangeSelectorButtons) {
        return {
          selected: getIndexOfSelectedRangeSelectorButton({ buttons: rangeSelectorButtons, id: selectedId }),
          buttons: rangeSelectorButtons
        }
      }

      if (rangeSelectorButtonsContent) {
        const rangeSeletorButtons = _(defaultRangeSeletorButtons)
          .map((rangeButton) => {
            const { id: rangeButtonId, ...restRangeButtonProps } = rangeButton
            return rangeSelectorButtonsContent[rangeButtonId]
              ? {
                  ...restRangeButtonProps,
                  id: rangeButtonId,
                  title: rangeSelectorButtonsContent[rangeButtonId],
                  text: rangeSelectorButtonsContent[rangeButtonId]
                }
              : ''
          })
          .without('')
          .value()

        return {
          selected: getIndexOfSelectedRangeSelectorButton({ buttons: rangeSeletorButtons, id: selectedId }),
          buttons: rangeSeletorButtons
        }
      }
    }

    return {
      selected: getIndexOfSelectedRangeSelectorButton(),
      buttons: defaultRangeSeletorButtons
    }
  }

  const getXAxisIntervalByDuration = (duration) => {
    const formattedDuration = _.isObject(duration) ? duration : { duration }
    const sortedFullTickIntervalRange = _(formattedDuration)
      .concat(yearsDurationTickIntervalMapping)
      .sort((a, b) => a.duration - b.duration)
      .value()

    const currentDataRangeIndex = _(sortedFullTickIntervalRange).findIndex(formattedDuration)
    const currentXAxisInterval =
      sortedFullTickIntervalRange[currentDataRangeIndex ? currentDataRangeIndex - 1 : currentDataRangeIndex + 1].tickInterval

    return tickIntervalMapping[currentXAxisInterval]
  }

  const getXAxisIntervalByPeriod = (xInterval) => {
    if (xInterval !== 'all') {
      return tickIntervalMapping[xInterval]
    }

    const dataTimeRange = _(options.series)
      .filter((dataItem) => dataItem.data.length)
      .map((dataItem) => ({
        duration: _.last(dataItem.data)[0] - _.head(dataItem.data)[0]
      }))
      .sort((a, b) => a.duration - b.duration)
      .last()

    return getXAxisIntervalByDuration(dataTimeRange)
  }

  const getSpecificRangeMinMaxPoints = (targetSeries, min, max) => {
    const copiedSeries = JSON.parse(JSON.stringify(targetSeries))

    const rangeSeries = copiedSeries.map((seriesItem) => {
      return {
        ...seriesItem,
        data: seriesItem.data.filter((dataItem) => dataItem[0] >= min && dataItem[0] <= max)
      }
    })

    const minMaxPoints = getMinMaxPoints(rangeSeries)
    const newSeries = formatMinMaxSeries(minMaxPoints)
    return newSeries
  }

  const getDateMsByDataType = ({ timestamp, flag, min, max }) => {
    const eightHoursMs = 1000 * 60 * 60 * 8 // Add 8 Hours for date time from Data picker
    let newMin = flag === 'min' ? timestamp + eightHoursMs : min
    let newMax = flag === 'max' ? timestamp + eightHoursMs : max
    let newMinMs = newMin
    let newMaxMs = newMax

    if (dataDurationType === 'monthly') {
      newMin = startOfDay(endOfMonth(newMin))
      newMax = startOfDay(endOfMonth(newMax))
      newMinMs = new Date(newMin).getTime()
      newMaxMs = new Date(newMax).getTime()
    }

    return {
      newMinMs,
      newMaxMs
    }
  }

  const getRangeSelector = (target, rangeButtonsConfig) => {
    const copiedRangeSelectorConfig = JSON.parse(JSON.stringify(rangeButtonsConfig))
    target.rangeSelector.buttonOptions.forEach((item, index) => {
      const currentRangeButton = target.rangeSelector.buttons[index]
      const currentRangButtonClassList = currentRangeButton.element.className.baseVal
      const isDisabled = currentRangButtonClassList.includes('highcharts-button-disabled')
      const isPressed = currentRangButtonClassList.includes('highcharts-button-pressed')

      copiedRangeSelectorConfig.buttons[index].disabled = isDisabled
      copiedRangeSelectorConfig.buttons[index].checked = isPressed
      if (isPressed) {
        copiedRangeSelectorConfig.selected = index
      }
    })

    return copiedRangeSelectorConfig
  }

  const getHighchartConfig = ({ rangeButtonsConfig }) => {
    return {
      chart: {
        // styledMode: true
        events: {
          load(event) {
            const newRangeSelector = getRangeSelector(event.target, rangeButtonsConfig)
            setRangeSelectorConfigRef(newRangeSelector)
          },
          render(event) {
            const newRangeSelector = getRangeSelector(event.target, rangeSelectorConfigRef.current)
            if (!_.isEqual(newRangeSelector, rangeSelectorConfigRef.current)) {
              setRangeSelectorConfigRef(newRangeSelector)
            }
          }
        }
      },
      rangeSelector: {
        // enabled: false,
        buttonTheme: {
          width: 'auto',
          fill: 'white',
          stroke: '#dcdee1',
          'stroke-width': 1,
          r: 0,
          style: {
            color: 'black'
          },
          states: {
            hover: {
              fill: '#ecf9fd',
              stroke: '#42bdea'
            },
            select: {
              fill: '#ecf9fd',
              stroke: '#42bdea',
              style: {
                fontWeight: 'normal'
              }
            }
          }
        },
        ...rangeButtonsConfig
      },
      legend: {
        enabled: true,
        align: 'left',
        x: 16
      },
      plotOptions: {
        series: {
          showInNavigator: true,
          dataGrouping: {
            approximation: 'open',
            enabled: false
          }
          // ,
          // marker: {
          //   enabled: false,
          //   lineColor: null,
          //   fillColor: 'transparent'
          // }
        }
      },
      credits: {
        enabled: false
      },
      navigator: {
        adaptToUpdatedData: false,
        xAxis: {
          tickInterval: tickIntervalMapping['1y'],
          labels: {
            formatter(navigatorXLabelFormatter) {
              return format(navigatorXLabelFormatter.value, 'yyyy')
            },
            overflow: 'allow'
          }
        },
        series: {
          marker: {
            enabled: false
            // ,
            // lineColor: null,
            // fillColor: 'transparent'
          }
        }
      },
      tooltip: {
        useHTML: true
      },
      xAxis: {
        minTickInterval: getXAxisIntervalByPeriod('1m'),
        tickInterval: defaultXAxisInterval,
        events: {
          setExtremes(xAxisEvent) {
            const self = this
            const { min, max } = xAxisEvent
            let currentTickInterval = ''
            if (xAxisEvent.trigger === 'rangeSelectorButton') {
              currentTickInterval = getXAxisIntervalByPeriod(xAxisEvent.rangeSelectorButton.xInterval)
            } else if (xAxisEvent.trigger === 'navigator') {
              currentTickInterval = getXAxisIntervalByDuration(max - min)
            }

            let newSeries = null

            if (displayMinMax) {
              newSeries = getSpecificRangeMinMaxPoints(newSeries, min, max)
            }

            if (newSeries) {
              self.chart.update({
                series: newSeries
              })
            }

            self.update({
              tickInterval: currentTickInterval
            })
          },
          afterSetExtremes(afterEvent) {
            const { dataMin, dataMax, userMin, userMax } = afterEvent
            if (afterEvent.trigger === 'navigator' || afterEvent.trigger === 'dateChange') {
              const newRangeSelectorButtons = updateRangeSelectorButtonsStatus({ isClean: true })
              setRangeSelectorConfigRef(newRangeSelectorButtons)
            }

            const defaultMin = userMin || dataMin
            const defaultMax = userMax || dataMax

            const newMin = defaultMin < dataMin ? dataMin : defaultMin

            const { newMinMs, newMaxMs } = getDateMsByDataType({ timestamp: '', flag: '', min: newMin, max: defaultMax })

            setDatePickerValues([newMinMs, newMaxMs])
            setMinMaxDates([
              {
                minDate: dataMin,
                maxDate: newMaxMs
              },
              {
                minDate: newMinMs,
                maxDate: dataMax
              }
            ])
          }
        },
        labels: {
          // staggerLines: 3,
          formatter(xLabelFormatter) {
            return format(xLabelFormatter.value, 'MM/dd')
          }
        }
      },
      yAxis: {
        opposite: false,
        plotBands: [
          {
            from: -10000000,
            to: 0
          }
        ]
      }
    }
  }

  const commonHandleDateChange = (timestamp, flag) => {
    const currentHighchartInstance = chartComponentRef.current.chart
    const { min, max } = currentHighchartInstance.xAxis[0]
    const { newMinMs, newMaxMs } = getDateMsByDataType({
      timestamp,
      flag,
      min,
      max
    })

    currentHighchartInstance.xAxis[0].setExtremes(newMinMs, newMaxMs, true, true, {
      trigger: 'dateChange'
    })
  }

  const handleStartDateChange = (date) => {
    commonHandleDateChange(parseFloat(format(date[0], 'TT')), 'min')
  }

  const handleEndDateChange = (date) => {
    commonHandleDateChange(parseFloat(format(date[0], 'TT')), 'max')
  }

  const getInitTimeRange = () => {
    let minTimestamp = 0
    let maxTimestamp = 0
    restOptions.series
      .filter((serie) => serie.data.length)
      .map((serie) => [_.first(serie.data)[0], _.last(serie.data)[0]])
      .forEach(([min, max]) => {
        if (minTimestamp) {
          minTimestamp = min > minTimestamp ? minTimestamp : min
        } else {
          minTimestamp = min
        }

        if (maxTimestamp) {
          maxTimestamp = max > maxTimestamp ? max : maxTimestamp
        } else {
          maxTimestamp = max
        }
      })

    return [minTimestamp, maxTimestamp]
  }

  useEffect(() => {
    let isOptionsChanged = false

    if (highchartConfig && chartComponentRef.current) {
      const copiedHighchartConfig = _.cloneDeep(highchartConfig)
      _.forIn(restOptions, (value, key) => {
        if (!_.isEqual(value, copiedHighchartConfig[key])) {
          isOptionsChanged = true
          if (key === 'series') {
            setDefaultSelectedRangeId(copiedHighchartConfig.rangeSelector.buttons[copiedHighchartConfig.rangeSelector.selected].id)
          }
        }
      })

      if (isOptionsChanged) {
        setHighchartConfig({ ...copiedHighchartConfig, ...restOptions })
      }
    }
  }, [restOptions])

  useEffect(() => {
    let chartInstance = null
    let allOptions = {}

    if (chartType === 'stockChart') {
      chartInstance = HighStockcharts

      const defaultRangeSelectorConfig = getRangeSelectorConfig()
      const formattedRangeSelectorConfigButtons = defaultRangeSelectorConfig.buttons.map((button) => {
        return {
          ...button,
          name: `${id ? `${id}-` : ''}date-range-toggle`,
          value: button.text,
          label: button.text,
          description: button.text,
          checked: button.id === rangeSelectorOptions.selectedId
        }
      })

      const formattedRangeSelectorConfig = {
        ...defaultRangeSelectorConfig,
        buttons: formattedRangeSelectorConfigButtons
      }

      defaultXAxisInterval = getXAxisIntervalByPeriod(formattedRangeSelectorConfig.buttons[formattedRangeSelectorConfig.selected].xInterval)

      allOptions = getHighchartConfig({ rangeButtonsConfig: formattedRangeSelectorConfig })

      const [minTimestamp, maxTimestamp] = getInitTimeRange()

      setDatePickerValues([minTimestamp, maxTimestamp])
      setMinMaxDates({
        minDate: minTimestamp,
        maxDate: maxTimestamp
      })
      setRangeSelectorConfig(formattedRangeSelectorConfig)
    } else {
      chartInstance = Highcharts
    }

    chartInstance.setOptions({
      ...topOptions
    })

    chartComponentRef.current = {
      chart: chartInstance
    }

    setHighchartConfig(_.defaultsDeep(restOptions, allOptions, commonConfig))
  }, [])

  return (
    <div className="fil-highcharts" id={`${id}__charts-container`}>
      {rangeSelectorConfigRef.current && (
        <div className="fil-highcharts-range-selectors">
          <Grid extraClasses="fil-highcharts-range-buttons align-justify align-middle">
            <Cell extraClasses={hasDatePicker ? 'xlarge-5 xxlarge-6' : 'xlarge-6'}>
              <Switch
                size="small"
                variations="chips"
                id={`${id}__date-range-buttons`}
                defaultSelectedId={defaultSelectedRangeId}
                items={rangeSelectorConfigRef.current.buttons}
                onChange={(e) => {
                  handleRangeButtonClick(e)
                }}
              />
            </Cell>
            {hasDatePicker && (
              <Cell extraClasses="fil-highcharts-date-picker xlarge-shrink">
                <DatePicker
                  id={`${id}__date-picker`}
                  mode="range"
                  {...commonOptions.highchartDatePickerConfig}
                  values={datePickerValues}
                  minMaxDates={minMaxDates}
                  handleChangeFuncs={[handleStartDateChange, handleEndDateChange]}
                />
              </Cell>
            )}
          </Grid>
        </div>
      )}
      {highchartConfig && (
        <HighchartsReact
          ref={chartComponentRef}
          highcharts={chartComponentRef.current.chart}
          options={highchartConfig}
          constructorType={chartType}
          {...restProps}
        />
      )}
      <div className="text-right">{updateDate}</div>
    </div>
  )
}

CustomHighchart.propTypes = {
  id: PropTypes.string.isRequired,
  options: PropTypes.object.isRequired,
  commonOptions: PropTypes.object,
  chartType: PropTypes.oneOfType(['chart', 'stockChart', 'mapChart', 'ganttChart']),
  extraClasses: PropTypes.string,
  onChartRangeChange: PropTypes.func,
  hasDateRange: PropTypes.bool,
  hasDatePicker: PropTypes.bool
}

CustomHighchart.defaultProps = {
  chartType: 'stockChart',
  extraClasses: '',
  commonOptions: {
    highchartDatePickerConfig: {}
  },
  onChartRangeChange: () => {},
  hasDateRange: true,
  hasDatePicker: true
}

export default CustomHighchart
